import React, { useState } from 'react';
import {
    Container,
    Typography,
    Paper,
    Box,
    Button,
    TextField,
    Alert,
    Grid,
    Card,
    CardContent,
    CardHeader,
    Divider,
    Avatar,
    IconButton,
    InputAdornment,
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    AccountCircle as AccountCircleIcon,
    Lock as LockIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { userAPI } from '../../services/api';

const ProfilePage: React.FC = () => {
    const { user } = useAuth();
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });

    const handlePasswordChange = (field: keyof typeof passwordForm) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setPasswordForm({
            ...passwordForm,
            [field]: event.target.value,
        });
        // Clear alert when user starts typing
        if (alert) setAlert(null);
    };

    const validatePasswordForm = () => {
        if (!passwordForm.currentPassword) {
            setAlert({ type: 'error', message: 'Current password is required' });
            return false;
        }
        if (!passwordForm.newPassword) {
            setAlert({ type: 'error', message: 'New password is required' });
            return false;
        }
        if (passwordForm.newPassword.length < 8) {
            setAlert({ type: 'error', message: 'New password must be at least 8 characters long' });
            return false;
        }
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            setAlert({ type: 'error', message: 'New passwords do not match' });
            return false;
        }
        if (passwordForm.currentPassword === passwordForm.newPassword) {
            setAlert({ type: 'error', message: 'New password must be different from current password' });
            return false;
        }
        return true;
    };

    const handlePasswordSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        
        if (!validatePasswordForm() || !user) return;

        setLoading(true);
        try {
            await userAPI.resetPassword(user.id, {
                currentPassword: passwordForm.currentPassword,
                newPassword: passwordForm.newPassword,
            });
            
            setAlert({ type: 'success', message: 'Password updated successfully!' });
            setPasswordForm({
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            });
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || 'Failed to update password';
            setAlert({ type: 'error', message: errorMessage });
        } finally {
            setLoading(false);
        }
    };

    const handleResetForm = () => {
        setPasswordForm({
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
        });
        setAlert(null);
    };

    if (!user) {
        return (
            <Container maxWidth="md">
                <Alert severity="error">User information not available</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Typography variant="h4" gutterBottom>
                User Profile
            </Typography>

            <Grid container spacing={3}>
                {/* User Information Card */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardHeader
                            avatar={
                                <Avatar sx={{ bgcolor: 'primary.main' }}>
                                    <AccountCircleIcon />
                                </Avatar>
                            }
                            title="Account Information"
                            subheader="Your account details"
                        />
                        <CardContent>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    label="First Name"
                                    value={user.firstName || ''}
                                    disabled
                                    fullWidth
                                />
                                <TextField
                                    label="Last Name"
                                    value={user.lastName || ''}
                                    disabled
                                    fullWidth
                                />
                                <TextField
                                    label="Email"
                                    value={user.email || ''}
                                    disabled
                                    fullWidth
                                />
                                <TextField
                                    label="Role"
                                    value={user.role || ''}
                                    disabled
                                    fullWidth
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Password Change Card */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardHeader
                            avatar={
                                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                    <LockIcon />
                                </Avatar>
                            }
                            title="Change Password"
                            subheader="Update your account password"
                        />
                        <CardContent>
                            <Box component="form" onSubmit={handlePasswordSubmit}>
                                {alert && (
                                    <Alert 
                                        severity={alert.type} 
                                        sx={{ mb: 2 }}
                                        onClose={() => setAlert(null)}
                                    >
                                        {alert.message}
                                    </Alert>
                                )}

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <TextField
                                        label="Current Password"
                                        type={showCurrentPassword ? 'text' : 'password'}
                                        value={passwordForm.currentPassword}
                                        onChange={handlePasswordChange('currentPassword')}
                                        required
                                        fullWidth
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                                        edge="end"
                                                    >
                                                        {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <TextField
                                        label="New Password"
                                        type={showNewPassword ? 'text' : 'password'}
                                        value={passwordForm.newPassword}
                                        onChange={handlePasswordChange('newPassword')}
                                        required
                                        fullWidth
                                        helperText="Must be at least 8 characters long"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                                        edge="end"
                                                    >
                                                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <TextField
                                        label="Confirm New Password"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        value={passwordForm.confirmPassword}
                                        onChange={handlePasswordChange('confirmPassword')}
                                        required
                                        fullWidth
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                        edge="end"
                                                    >
                                                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <Divider sx={{ my: 1 }} />

                                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                        <Button
                                            variant="outlined"
                                            onClick={handleResetForm}
                                            disabled={loading}
                                        >
                                            Reset Form
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={loading}
                                        >
                                            {loading ? 'Updating...' : 'Update Password'}
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Container>
    );
};

export default ProfilePage;
